// A leaf module: type-only Mantine imports, so the app-shell chunk does not pull in
// Menu, Collapse or NumberInput just to configure them.
import type {
  CollapseProps,
  MantineThemeComponents,
  MenuProps,
  NumberInputProps,
} from '@mantine/core';

type NumberInputMode = 'text' | 'numeric';

/**
 * Mantine NumberInput sets inputMode='decimal', whose iOS keypad has no minus key and only
 * the region's decimal separator. Use the numeric keypad, or the full keyboard on
 * iPhone/iPod, whose numeric keypad has no minus or decimal key.
 */
export const numberInputMode = (platform: string): NumberInputMode =>
  /iPhone|iPod/.test(platform) ? 'text' : 'numeric';

/**
 * Keys typed as the decimal point: the locale's separator plus '.', react-number-format's
 * own default. Mantine's default, ['.', ','], turns a typed English thousands comma into a
 * decimal point.
 */
export const numberInputDecimalAliases = (
  decimalSeparator: string
): string[] => [...new Set([decimalSeparator, '.'])];

const currentPlatform = (): string =>
  typeof navigator === 'undefined' ? '' : navigator.platform;

type NumberInputThemeDefaults = Pick<
  NumberInputProps,
  'decimalSeparator' | 'allowedDecimalSeparators' | 'inputMode'
>;

const numberInputThemeDefaults = (
  decimalSeparator: string,
  platform: string = currentPlatform()
): NumberInputThemeDefaults => ({
  decimalSeparator,
  allowedDecimalSeparators: numberInputDecimalAliases(decimalSeparator),
  inputMode: numberInputMode(platform),
});

/**
 * Mantine Menu passes returnFocus to its Popover, so closing a menu pulls focus back to
 * the target ~10ms later, blurring an input a menu item just focused or a field the user
 * clicked into while the menu was open.
 */
const menuDefaults: Partial<MenuProps> = { returnFocus: false };

/**
 * Mantine hides a closed Collapse with display:none until a frame after it opens, so
 * content measured on open (autosize Textarea) reads too short. keepMounted closes it to
 * height 0 with overflow hidden instead. Accordion.Panel and NavLink render through
 * Collapse and pick this up too.
 */
const collapseDefaults: Partial<CollapseProps> = { keepMounted: true };

/**
 * Theme `components` entries shared by every MantineProvider. `numberInputExtras` (e.g. a
 * paste handler) is merged over the NumberInput defaults.
 */
export const mantineComponentDefaults = (
  decimalSeparator = '.',
  numberInputExtras: Partial<NumberInputProps> = {}
): MantineThemeComponents => ({
  Menu: { defaultProps: menuDefaults },
  Collapse: { defaultProps: collapseDefaults },
  NumberInput: {
    defaultProps: {
      ...numberInputThemeDefaults(decimalSeparator),
      ...numberInputExtras,
    },
  },
});
